import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, customerApi } from '../services/api'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import Modal from '../components/common/Modal'
import toast from 'react-hot-toast'

export default function CreateTimesheet() {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [jobs, setJobs] = useState([])
    const [qbCustomers, setQbCustomers] = useState([])
    const [manualCustomers, setManualCustomers] = useState([])
    const [allCustomers, setAllCustomers] = useState([])
    const [selectedCustomer, setSelectedCustomer] = useState(null)
    const [customerJobs, setCustomerJobs] = useState([])
    const [searchCustomer, setSearchCustomer] = useState('')
    const [searchJob, setSearchJob] = useState('')

    // New customer modal
    const [showNewCustomer, setShowNewCustomer] = useState(false)
    const [savingCustomer, setSavingCustomer] = useState(false)
    const [newCustomer, setNewCustomer] = useState({
        customer: '',
        firstName: '',
        lastName: '',
        jobSite: '',
    })

    useEffect(() => {
        loadData()
    }, [])

    async function loadData() {
        setLoading(true)
        try {
            const response = await api.get('/api/jobs/all-customers')
            if (response.success) {
                const allJobs = response.jobs || []
                setJobs(allJobs)
                setQbCustomers(response.qbCustomers || [])
                setManualCustomers(response.manualCustomers || [])

                // Combine and sort all customers
                const qbNames = response.qbCustomers || []
                const manualNames = (response.manualCustomers || []).map(c => ({
                    name: c.customerName,
                    isManual: true,
                    data: c
                }))
                const qbNameObjects = qbNames.map(name => ({
                    name,
                    isManual: false
                }))

                // Combine, remove duplicates, sort
                const combined = [...qbNameObjects]
                manualNames.forEach(m => {
                    if (!combined.find(c => c.name.toLowerCase() === m.name.toLowerCase())) {
                        combined.push(m)
                    }
                })
                combined.sort((a, b) => a.name.localeCompare(b.name))

                setAllCustomers(combined)
            } else {
                toast.error('Failed to load data')
            }
        } catch (error) {
            toast.error('Error loading data')
        }
        setLoading(false)
    }

    function handleCustomerSelect(customer) {
        if (customer.isManual) {
            // Manual customer - go straight to timesheet with their info
            navigate('/timesheet/new', {
                state: {
                    customer: customer.data.customerName || '',
                    jobSite: customer.data.jobSite || '',
                    contact: `${customer.data.firstName || ''} ${customer.data.lastName || ''}`.trim(),
                    customerRequirement: '',
                }
            })
        } else {
            // QB customer - show their jobs
            setSelectedCustomer(customer.name)
            const filtered = jobs
                .filter(j => j.customer === customer.name)
                .sort((a, b) => (b.jobName || b.jobDescription || '').localeCompare(a.jobName || a.jobDescription || ''))
            setCustomerJobs(filtered)
            setSearchJob('')
        }
    }

    function handleJobSelect(job) {
        navigate('/timesheet/new', {
            state: {
                customer: job.customer || '',
                jobSite: job.jobSite || '',
                contact: job.contactName || '',
                customerRequirement: job.jobDescription || '',
            }
        })
    }

    function handleCreateWithoutJob() {
        const mostRecentJob = customerJobs[0] 

        navigate('/timesheet/new', {
            state: {
                customer: selectedCustomer,
                jobSite: mostRecentJob?.jobSite || '',
                contact: mostRecentJob?.contactName || '',
                customerRequirement: '',
            }
        })
    }

    function handleBack() {
        setSelectedCustomer(null)
        setCustomerJobs([])
        setSearchJob('')
    }

    function handleNewCustomerChange(field, value) {
        setNewCustomer(prev => ({ ...prev, [field]: value }))
    }

    async function handleSaveNewCustomer() {
        if (!newCustomer.customer.trim()) {
            toast.error('Customer name is required')
            return
        }

        setSavingCustomer(true)
        try {
            const response = await customerApi.create(newCustomer)
            if (response.success) {
                toast.success('Customer added')
                setShowNewCustomer(false)

                // Navigate to timesheet with new customer info
                navigate('/timesheet/new', {
                    state: {
                        customer: newCustomer.customer,
                        jobSite: newCustomer.jobSite || '',
                        contact: `${newCustomer.firstName || ''} ${newCustomer.lastName || ''}`.trim(),
                        customerRequirement: '',
                    }
                })
            } else {
                toast.error(response.message || 'Failed to add customer')
            }
        } catch (error) {
            toast.error('Error adding customer')
        }
        setSavingCustomer(false)
    }

    function openNewCustomerModal() {
        setNewCustomer({
            customer: '',
            firstName: '',
            lastName: '',
            jobSite: '',
        })
        setShowNewCustomer(true)
    }

    // Filter customers by search
    const filteredCustomers = allCustomers.filter(c =>
        c.name.toLowerCase().includes(searchCustomer.toLowerCase())
    )

    // Filter jobs by search
    const filteredJobs = customerJobs.filter(j =>
        (j.jobDescription || '').toLowerCase().includes(searchJob.toLowerCase()) ||
        (j.jobName || '').toLowerCase().includes(searchJob.toLowerCase()) ||
        (j.jobSite || '').toLowerCase().includes(searchJob.toLowerCase())
    )

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    return (
        <div>
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">
                        {selectedCustomer ? `Jobs for ${selectedCustomer}` : 'Select Customer'}
                    </h1>
                    <p className="text-blue-100 mt-1">
                        {selectedCustomer
                            ? `${filteredJobs.length} jobs found`
                            : `${filteredCustomers.length} customers`}
                    </p>
                </div>
                <div className="flex gap-2">
                    {selectedCustomer ? (
                        <Button variant="secondary" onClick={handleBack}>
                            ← Back
                        </Button>
                    ) : (
                        <Button onClick={openNewCustomerModal}>
                            + New Customer
                        </Button>
                    )}
                </div>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                <Input
                    placeholder={selectedCustomer ? "Search jobs..." : "Search customers..."}
                    value={selectedCustomer ? searchJob : searchCustomer}
                    onChange={(e) => selectedCustomer
                        ? setSearchJob(e.target.value)
                        : setSearchCustomer(e.target.value)
                    }
                />
            </div>

            {/* List */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                {!selectedCustomer ? (
                    // Customer List
                    <div className="divide-y max-h-[60vh] overflow-y-auto">
                        {filteredCustomers.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <p className="text-4xl mb-2">🏢</p>
                                <p>No customers found</p>
                                <Button
                                    variant="outline"
                                    className="mt-4"
                                    onClick={openNewCustomerModal}
                                >
                                    Add New Customer
                                </Button>
                            </div>
                        ) : (
                            filteredCustomers.map((customer, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleCustomerSelect(customer)}
                                    className="w-full text-left p-4 hover:bg-blue-50 transition-colors flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${customer.isManual ? 'bg-green-500' : 'bg-rdi-blue'}`}>
                                            {customer.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-800">{customer.name}</span>
                                            {customer.isManual && (
                                                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                                    New
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-gray-400">
                                        {!customer.isManual && (
                                            <span className="text-sm">
                                                {jobs.filter(j => j.customer === customer.name).length} jobs
                                            </span>
                                        )}
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                ) : (
                    // Job List for selected customer
                    <div className="max-h-[60vh] overflow-y-auto">
                        {/* Create without Job Number option - always show at top */}
                        <div className="p-4 bg-blue-50 border-b-2 border-blue-200">
                            <button
                                onClick={handleCreateWithoutJob}
                                className="w-full text-left p-4 bg-white rounded-lg border-2 border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white">
                                            <span className="text-xl">+</span>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-800">Create without Job Number</p>
                                            <p className="text-sm text-gray-500">No job in QuickBooks yet? Start a timesheet anyway.</p>
                                        </div>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </button>
                        </div>

                        {/* Job list */}
                        <div className="divide-y">
                            {filteredJobs.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="text-4xl mb-2">📋</p>
                                    <p>No jobs found for this customer</p>
                                    <p className="text-sm mt-2">Use the option above to create a timesheet without a job number</p>
                                </div>
                            ) : (
                                filteredJobs.map((job, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleJobSelect(job)}
                                        className="w-full text-left p-4 hover:bg-blue-50 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-semibold text-rdi-blue text-lg">
                                                    {job.jobDescription || job.jobName || 'Untitled Job'}
                                                </p>
                                                {job.jobSite && (
                                                    <p className="text-sm text-gray-500 mt-1">📍 {job.jobSite}</p>
                                                )}
                                                {job.contactName && (
                                                    <p className="text-sm text-gray-500">👤 {job.contactName}</p>
                                                )}
                                            </div>
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* New Customer Modal */}
            <Modal
                isOpen={showNewCustomer}
                onClose={() => setShowNewCustomer(false)}
                title="Add New Customer"
                size="md"
            >
                <div className="space-y-4">
                    <Input
                        label="Customer Name"
                        value={newCustomer.customer}
                        onChange={(e) => handleNewCustomerChange('customer', e.target.value)}
                        required
                        placeholder="Company or customer name"
                    />

                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label="Contact First Name"
                            value={newCustomer.firstName}
                            onChange={(e) => handleNewCustomerChange('firstName', e.target.value)}
                            placeholder="First name"
                        />
                        <Input
                            label="Contact Last Name"
                            value={newCustomer.lastName}
                            onChange={(e) => handleNewCustomerChange('lastName', e.target.value)}
                            placeholder="Last name"
                        />
                    </div>

                    <Input
                        label="Job Site / Address"
                        value={newCustomer.jobSite}
                        onChange={(e) => handleNewCustomerChange('jobSite', e.target.value)}
                        placeholder="Address"
                    />
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                    <Button variant="secondary" onClick={() => setShowNewCustomer(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveNewCustomer} loading={savingCustomer}>
                        Add & Create Timesheet
                    </Button>
                </div>
            </Modal>
        </div>
    )
}